import { useState, useEffect, useCallback, useRef } from "react";
import { V } from "../theme.js";
import { OptBtn, AdjRow, Modal } from "../ui/components.jsx";
import { DEV_DEFAULTS } from "../engine/develop.js";

const HSL_RANGES = ["reds","oranges","yellows","greens","aquas","blues","purples","magentas"];
const HSL_COLORS = ["#f87171","#fb923c","#fbbf24","#4ade80","#22d3ee","#60a5fa","#a78bfa","#f472b6"];

function Section({ label, open, onToggle, children }) {
  return (
    <div style={{ marginBottom:4 }}>
      <div onClick={onToggle} style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 0", cursor:"pointer", userSelect:"none" }}>
        <span style={{ fontSize:9, color:V.ghost, transition:"transform 0.15s", transform:open?"rotate(90deg)":"rotate(0)" }}>▶</span>
        <span style={{ fontSize:11, fontWeight:500, color:V.text }}>{label}</span>
      </div>
      {open && <div style={{ paddingLeft:8 }}>{children}</div>}
    </div>
  );
}

/**
 * DevelopDialog — Camera RAW develop panel.
 * Non-destructive: applies pipeline on commit, stores settings for re-editing.
 */
export function DevelopDialog({ open, engRef, onCommit, onClose, forceUpdate }) {
  const [params, setParams] = useState({ ...DEV_DEFAULTS });
  const [sections, setSections] = useState({ basic:true, wb:false, curve:false, hsl:false, split:false, detail:false, effects:false });
  const previewStarted = useRef(false);

  const set = useCallback((k, v) => setParams(p => ({ ...p, [k]: v })), []);
  const setHSL = useCallback((group, range, v) => setParams(p => ({ ...p, [group]: { ...p[group], [range]: v } })), []);
  const toggleSection = useCallback((s) => setSections(prev => ({ ...prev, [s]: !prev[s] })), []);

  // Start preview backup on open
  useEffect(() => {
    if (!open) { previewStarted.current = false; return; }
    const eng = engRef?.current;
    if (!eng) return;
    if (!previewStarted.current) {
      eng.filterPreviewBegin();
      previewStarted.current = true;
    }
  }, [open, engRef]);

  // Live preview — restore from backup, apply develop pipeline
  useEffect(() => {
    if (!open || !previewStarted.current) return;
    const eng = engRef?.current;
    if (!eng || !eng.s._filterBackup) return;
    const L = eng.s.layers[eng.s.act];
    if (!L) return;
    // Restore original
    L.x.putImageData(eng.s._filterBackup, 0, 0);
    // Apply develop to a copy
    const id = L.x.getImageData(0, 0, eng.s.w, eng.s.h);
    import("../engine/develop.js").then(({ applyDevelop }) => {
      applyDevelop(id, params);
      L.x.putImageData(id, 0, 0);
      L._glDirty = true; L._fxDirty = true;
      eng.s.dirty = true;
      forceUpdate?.();
    });
  }, [open, params, engRef, forceUpdate]);

  const handleCommit = useCallback(() => {
    const eng = engRef?.current;
    if (eng) eng.filterPreviewCommit("Develop");
    previewStarted.current = false;
    onCommit(params);
    onClose();
  }, [params, onCommit, onClose, engRef]);

  const handleCancel = useCallback(() => {
    const eng = engRef?.current;
    if (eng) eng.filterPreviewCancel();
    previewStarted.current = false;
    onClose();
    forceUpdate?.();
  }, [engRef, onClose, forceUpdate]);

  const handleReset = useCallback(() => setParams({ ...DEV_DEFAULTS }), []);

  if (!open) return null;

  return (
    <Modal onClose={handleCancel}>
      <div style={{ maxHeight:"calc(100vh - 100px)", overflowY:"auto" }}>
        <div style={{ fontSize:13, fontWeight:700, color:"#ccc", marginBottom:10, display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ color:"#a78bfa" }}>◉</span> Develop
        </div>

        {/* ── Basic ── */}
        <Section label="Basic" open={sections.basic} onToggle={() => toggleSection("basic")}>
          <AdjRow label="Exposure" value={Math.round(params.exposure*100)} min={-500} max={500} onChange={v => set("exposure", v/100)} fmt={v => (v>0?"+":"")+(v/100).toFixed(2)+" EV"}/>
          <AdjRow label="Contrast" value={params.contrast} min={-100} max={100} onChange={v => set("contrast", v)}/>
          <AdjRow label="Highlights" value={params.highlights} min={-100} max={100} onChange={v => set("highlights", v)}/>
          <AdjRow label="Shadows" value={params.shadows} min={-100} max={100} onChange={v => set("shadows", v)}/>
          <AdjRow label="Whites" value={params.whites} min={-100} max={100} onChange={v => set("whites", v)}/>
          <AdjRow label="Blacks" value={params.blacks} min={-100} max={100} onChange={v => set("blacks", v)}/>
        </Section>

        {/* ── White Balance ── */}
        <Section label="White balance" open={sections.wb} onToggle={() => toggleSection("wb")}>
          <AdjRow label="Temperature" value={params.temperature} min={-100} max={100} onChange={v => set("temperature", v)} fmt={v => (v>0?"+":"")+v}/>
          <AdjRow label="Tint" value={params.tint} min={-100} max={100} onChange={v => set("tint", v)} fmt={v => (v>0?"+":"")+v}/>
        </Section>

        {/* ── Tone Curve ── */}
        <Section label="Tone curve" open={sections.curve} onToggle={() => toggleSection("curve")}>
          <ToneCurveEditor points={params.curvePoints} onChange={pts => set("curvePoints", pts)}/>
        </Section>

        {/* ── HSL ── */}
        <Section label="HSL / Color" open={sections.hsl} onToggle={() => toggleSection("hsl")}>
          {HSL_RANGES.map((r, i) => (
            <div key={r} style={{ marginBottom:6 }}>
              <div style={{ fontSize:10, color:HSL_COLORS[i], marginBottom:2, textTransform:"capitalize" }}>{r}</div>
              <AdjRow label="Hue" value={params.hslHue[r]||0} min={-30} max={30} onChange={v => setHSL("hslHue", r, v)} fmt={v => v+"°"}/>
              <AdjRow label="Sat" value={params.hslSat[r]||0} min={-100} max={100} onChange={v => setHSL("hslSat", r, v)}/>
              <AdjRow label="Lum" value={params.hslLum[r]||0} min={-100} max={100} onChange={v => setHSL("hslLum", r, v)}/>
            </div>
          ))}
        </Section>

        {/* ── Split Toning ── */}
        <Section label="Split toning" open={sections.split} onToggle={() => toggleSection("split")}>
          <div style={{ fontSize:10, color:V.muted, marginBottom:4 }}>Highlights</div>
          <AdjRow label="Hue" value={params.splitHighHue} min={0} max={360} onChange={v => set("splitHighHue", v)} fmt={v => v+"°"}/>
          <AdjRow label="Saturation" value={params.splitHighSat} min={0} max={100} onChange={v => set("splitHighSat", v)}/>
          <div style={{ fontSize:10, color:V.muted, marginBottom:4, marginTop:8 }}>Shadows</div>
          <AdjRow label="Hue" value={params.splitShadowHue} min={0} max={360} onChange={v => set("splitShadowHue", v)} fmt={v => v+"°"}/>
          <AdjRow label="Saturation" value={params.splitShadowSat} min={0} max={100} onChange={v => set("splitShadowSat", v)}/>
          <div style={{ marginTop:8 }}>
            <AdjRow label="Balance" value={params.splitBalance} min={-100} max={100} onChange={v => set("splitBalance", v)}/>
          </div>
        </Section>

        {/* ── Detail ── */}
        <Section label="Detail" open={sections.detail} onToggle={() => toggleSection("detail")}>
          <div style={{ fontSize:10, color:V.muted, marginBottom:4 }}>Sharpening</div>
          <AdjRow label="Amount" value={params.sharpenAmount} min={0} max={150} onChange={v => set("sharpenAmount", v)}/>
          <AdjRow label="Radius" value={Math.round(params.sharpenRadius*10)} min={5} max={30} onChange={v => set("sharpenRadius", v/10)} fmt={v => (v/10).toFixed(1)+"px"}/>
          <div style={{ fontSize:10, color:V.muted, marginBottom:4, marginTop:8 }}>Noise reduction</div>
          <AdjRow label="Luminance" value={params.noiseReduction} min={0} max={100} onChange={v => set("noiseReduction", v)}/>
        </Section>

        {/* ── Effects ── */}
        <Section label="Effects" open={sections.effects} onToggle={() => toggleSection("effects")}>
          <AdjRow label="Dehaze" value={params.dehaze} min={-100} max={100} onChange={v => set("dehaze", v)}/>
          <AdjRow label="Grain" value={params.grainAmount} min={0} max={100} onChange={v => set("grainAmount", v)}/>
          <AdjRow label="Vignette" value={params.vignetteAmount} min={-100} max={100} onChange={v => set("vignetteAmount", v)}/>
        </Section>
      </div>

      <div style={{ display:"flex", gap:6, justifyContent:"flex-end", paddingTop:8 }}>
        <OptBtn onClick={handleReset}>Reset</OptBtn>
        <OptBtn onClick={handleCancel}>Cancel</OptBtn>
        <OptBtn accent onClick={handleCommit}>Apply</OptBtn>
      </div>
    </Modal>
  );
}

// ── Interactive Tone Curve Editor ──
function ToneCurveEditor({ points, onChange }) {
  const svgRef = useRef(null);
  const dragging = useRef(null);
  const W = 200, H = 200, PAD = 8;

  const toSvg = useCallback((pt) => [PAD + pt[0] / 255 * (W - PAD * 2), H - PAD - pt[1] / 255 * (H - PAD * 2)], []);
  const fromSvg = useCallback((sx, sy) => {
    const x = Math.max(0, Math.min(255, Math.round((sx - PAD) / (W - PAD * 2) * 255)));
    const y = Math.max(0, Math.min(255, Math.round((H - PAD - sy) / (H - PAD * 2) * 255)));
    return [x, y];
  }, []);

  const getSvgCoords = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return [0, 0];
    const r = svg.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  }, []);

  const onPointerDown = useCallback((e, idx) => {
    e.preventDefault(); e.stopPropagation();
    dragging.current = idx;
    svgRef.current?.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e) => {
    if (dragging.current == null) return;
    const [sx, sy] = getSvgCoords(e);
    const pt = fromSvg(sx, sy);
    const idx = dragging.current;
    if (idx === 0) pt[0] = 0;
    if (idx === points.length - 1) pt[0] = 255;
    const newPts = points.map((p, i) => i === idx ? pt : p);
    onChange(newPts);
  }, [points, onChange, getSvgCoords, fromSvg]);

  const onPointerUp = useCallback(() => { dragging.current = null; }, []);

  const onSvgClick = useCallback((e) => {
    if (dragging.current != null) return;
    const [sx, sy] = getSvgCoords(e);
    const pt = fromSvg(sx, sy);
    for (const p of points) { if (Math.abs(p[0] - pt[0]) < 15) return; }
    onChange([...points, pt].sort((a, b) => a[0] - b[0]));
  }, [points, onChange, getSvgCoords, fromSvg]);

  const onCtxMenu = useCallback((e, idx) => {
    e.preventDefault();
    if (idx === 0 || idx === points.length - 1) return;
    onChange(points.filter((_, i) => i !== idx));
  }, [points, onChange]);

  const sorted = [...points].sort((a, b) => a[0] - b[0]);
  let pathD = "";
  sorted.forEach((pt, i) => { const [sx, sy] = toSvg(pt); pathD += i === 0 ? `M${sx},${sy}` : `L${sx},${sy}`; });

  const presets = [
    { l:"Linear", pts:[[0,0],[255,255]] },
    { l:"S-Curve", pts:[[0,0],[64,48],[128,128],[192,208],[255,255]] },
    { l:"Fade", pts:[[0,20],[64,60],[128,128],[192,192],[255,240]] },
    { l:"Hi-Con", pts:[[0,0],[64,16],[128,128],[192,240],[255,255]] },
  ];

  return (
    <div>
      <svg ref={svgRef} width={W} height={H}
        style={{ background:"#1a1a1a", borderRadius:4, cursor:"crosshair", display:"block", marginBottom:6 }}
        onClick={onSvgClick} onPointerMove={onPointerMove} onPointerUp={onPointerUp}>
        {[0.25,0.5,0.75].map(f => (
          <g key={f}>
            <line x1={PAD+f*(W-PAD*2)} y1={PAD} x2={PAD+f*(W-PAD*2)} y2={H-PAD} stroke="#333" strokeWidth="0.5"/>
            <line x1={PAD} y1={PAD+f*(H-PAD*2)} x2={W-PAD} y2={PAD+f*(H-PAD*2)} stroke="#333" strokeWidth="0.5"/>
          </g>
        ))}
        <line x1={PAD} y1={H-PAD} x2={W-PAD} y2={PAD} stroke="#444" strokeWidth="0.5"/>
        <path d={pathD} fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        {sorted.map((pt, i) => {
          const [sx, sy] = toSvg(pt);
          return <circle key={i} cx={sx} cy={sy} r={5} fill="#1e1e1e" stroke="#a78bfa" strokeWidth="1.5"
            style={{ cursor:"grab" }} onPointerDown={e => onPointerDown(e, i)} onContextMenu={e => onCtxMenu(e, i)}/>;
        })}
      </svg>
      <div style={{ display:"flex", gap:3, flexWrap:"wrap" }}>
        {presets.map(pr => (
          <div key={pr.l} onClick={() => onChange(pr.pts)}
            style={{ padding:"2px 7px", fontSize:9, borderRadius:3, cursor:"pointer", fontFamily:V.mono,
              background:V.panelActive, border:`1px solid ${V.panelBorder2}`, color:V.muted }}>
            {pr.l}
          </div>
        ))}
      </div>
      <div style={{ fontSize:9, color:V.ghost, marginTop:4 }}>Click to add. Right-click to remove. Drag to adjust.</div>
    </div>
  );
}
